import React from "react";
import { Num } from "./Num";

/**
 * The segment / channel / route picker control.
 *
 * The spec's accessibility rule:
 *
 *   "Segment pills are a radio group; the live count is inside the accessible
 *    name ('Active, 8,412 users')."
 *
 * The visible count is a separate DOM node from the label, so without an
 * explicit `aria-label` a screen reader hears "Active 8,412" with no unit and
 * no pause — or, worse, "Active —". The label is built here, once, so no
 * caller can get it wrong.
 */

/** Full class strings only. Tailwind's purger cannot see constructed names. */
const BASE =
  "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors";

const SELECTED = "border-orange-primary bg-light-orange text-bc-accent-text";
const UNSELECTED = "border-bc-border bg-white text-bc-text-2";

/** Hover only earns its place on a pill you can actually pick. */
const HOVER = "hover:border-bc-soft";

const FOCUS =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-1";

const DISABLED = "cursor-not-allowed opacity-50";

/**
 * Wrapper for a set of `Pill`s. A real radio group, so arrow-key semantics and
 * the "one of N" announcement come from the platform rather than from us.
 *
 * @param {Object} props
 * @param {string} props.label - Accessible name for the group, e.g. "Push audience".
 * @param {React.ReactNode} props.children - `Pill` elements.
 * @param {string} [props.className] - Extra classes, merged last.
 * @returns {React.ReactElement}
 */
export const PillGroup = ({ label, children, className = "" }) => (
  <div
    role="radiogroup"
    aria-label={label}
    className={`flex flex-wrap gap-2 ${className}`}
  >
    {children}
  </div>
);

/**
 * Is this count renderable as a number? `null` still renders (as an em dash),
 * but it must not appear in the accessible name.
 *
 * @param {unknown} count
 * @returns {boolean}
 */
const hasCountValue = (count) =>
  typeof count === "number" && Number.isFinite(count);

/**
 * One option in a `PillGroup`.
 *
 * @param {Object} props
 * @param {boolean} [props.selected=false] - Whether this option is chosen.
 * @param {() => void} [props.onSelect] - Called on click. Never called when `disabled`.
 * @param {string} props.children - The visible label. MUST be a plain string when `count` is used — it is spliced into the accessible name.
 * @param {number|null} [props.count] - Live audience size. Pass `null` while loading; renders `—`. Omit entirely for a countless pill.
 * @param {string} [props.countLabel='users'] - Unit spoken after the count, e.g. "contacts".
 * @param {boolean} [props.disabled=false] - Renders dimmed and non-interactive.
 * @param {string} [props.className] - Extra classes, merged last.
 * @returns {React.ReactElement}
 */
export const Pill = ({
  selected = false,
  onSelect,
  children,
  count,
  countLabel = "users",
  disabled = false,
  className = "",
}) => {
  const showsCount = count !== undefined;
  const text = typeof children === "string" ? children : null;

  // "Active, 8,412 users". No count -> no trailing comma. A non-string child
  // means we cannot build a name safely, so we let the content supply it.
  let ariaLabel;
  if (text !== null) {
    ariaLabel = hasCountValue(count)
      ? `${text}, ${count.toLocaleString()} ${countLabel}`
      : text;
  }

  const stateClass = selected ? SELECTED : UNSELECTED;
  const interactionClass = disabled ? DISABLED : `${FOCUS} ${selected ? "" : HOVER}`;

  const handleClick = () => {
    if (disabled) return;
    if (onSelect) onSelect();
  };

  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={handleClick}
      className={`${BASE} ${stateClass} ${interactionClass} ${className}`}
    >
      <span>{children}</span>
      {showsCount && <Num value={count} />}
    </button>
  );
};
